import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast, Toaster } from 'sonner';
import {
  ShieldCheck, Lock, Thermometer, ClipboardCheck, AlertTriangle, CheckCircle2,
  Brush, X, Sun, Moon, ClipboardList,
} from 'lucide-react';
import StockTakeTab from '../components/kiosk/StockTakeTab';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Public kiosk page: /kiosk/:slug
 * - Bookmarkable on an iPad by the till
 * - PIN gate → Tabbed Today screen (Temps / Cleaning / Stock take)
 * - Per-employee light/dark theme
 * - Auto-lock after N seconds inactivity (from server config)
 */
export default function KioskPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState(null);
  const [session, setSession] = useState(null);   // { session_token, employee_name, is_manager, auto_lock_seconds, theme }
  const [pinInput, setPinInput] = useState('');
  const [pinBusy, setPinBusy] = useState(false);
  const [today, setToday] = useState(null);
  const [theme, setTheme] = useState('dark');
  const [tab, setTab] = useState('temps');

  // Temperature entry dialog
  const [tempDialog, setTempDialog] = useState(null);
  const [tempReading, setTempReading] = useState('');
  const [tempNote, setTempNote] = useState('');

  // Corrective-action resolve dialog
  const [caDialog, setCaDialog] = useState(null);
  const [caReason, setCaReason] = useState('');

  // Cleaning task dialog
  const [taskDialog, setTaskDialog] = useState(null);
  const [taskMissedReason, setTaskMissedReason] = useState('');

  // Sign-off dialog
  const [signoffDialog, setSignoffDialog] = useState(false);
  const [signoffNotes, setSignoffNotes] = useState('');

  const lastActivityRef = useRef(Date.now());
  const isLight = theme === 'light';
  const t = (dark, light) => (isLight ? light : dark);

  const kApi = useCallback(() => axios.create({
    baseURL: `${API}/api/kiosk/${slug}`,
    headers: session?.session_token ? { 'X-Kiosk-Token': session.session_token } : {},
  }), [slug, session]);

  useEffect(() => {
    axios.get(`${API}/api/kiosk/${slug}/info`)
      .then(res => setInfo(res.data))
      .catch(() => setInfo({ error: 'Kiosk not found' }));
  }, [slug]);

  const refreshToday = useCallback(async () => {
    if (!session) return;
    try {
      const res = await kApi().get('/today');
      setToday(res.data);
      // Keep theme in sync with server state (in case employee switched on another iPad)
      const serverTheme = res.data?.session?.theme;
      if (serverTheme && serverTheme !== theme) setTheme(serverTheme);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.info('Kiosk locked — enter PIN');
        setSession(null); setToday(null);
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to load');
      }
    }
  }, [session, kApi, theme]);

  useEffect(() => { refreshToday(); }, [refreshToday]);

  // Restore last active tab on unlock.
  useEffect(() => {
    if (session) {
      const stored = sessionStorage.getItem(`kiosk_tab_${slug}`);
      if (stored) setTab(stored);
    }
  }, [session, slug]);
  useEffect(() => {
    if (session) sessionStorage.setItem(`kiosk_tab_${slug}`, tab);
  }, [tab, slug, session]);

  // Inactivity auto-lock
  useEffect(() => {
    if (!session) return;
    const bump = () => { lastActivityRef.current = Date.now(); };
    ['mousedown', 'touchstart', 'keydown'].forEach(ev => window.addEventListener(ev, bump));
    const interval = setInterval(() => {
      const idle = (Date.now() - lastActivityRef.current) / 1000;
      if (idle > (session.auto_lock_seconds || 300)) {
        setSession(null); setToday(null);
        toast.info('Kiosk locked after inactivity');
      }
    }, 5000);
    return () => {
      ['mousedown', 'touchstart', 'keydown'].forEach(ev => window.removeEventListener(ev, bump));
      clearInterval(interval);
    };
  }, [session]);

  const submitPin = async () => {
    if (!/^\d{4,8}$/.test(pinInput)) return toast.error('4-8 digit PIN');
    setPinBusy(true);
    try {
      const res = await axios.post(`${API}/api/kiosk/${slug}/pin-verify`, { pin: pinInput });
      setSession(res.data);
      setTheme(res.data.theme || 'dark');
      setPinInput('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'PIN not recognised');
    } finally {
      setPinBusy(false);
    }
  };

  const lock = () => { setSession(null); setToday(null); };

  const toggleTheme = async () => {
    const next = isLight ? 'dark' : 'light';
    setTheme(next); // optimistic
    try {
      await kApi().put('/theme', { theme: next });
    } catch (err) {
      setTheme(theme); // rollback
      toast.error('Failed to save theme');
    }
  };

  const submitTemperature = async () => {
    const reading = parseFloat(tempReading);
    if (Number.isNaN(reading)) return toast.error('Enter a numeric reading');
    try {
      const res = await kApi().post('/checks/temperature', {
        asset_id: tempDialog.id, reading_c: reading, note: tempNote || null,
      });
      toast.success(res.data.passed ? `Pass — ${reading}°C` : `Fail — ${reading}°C out of range`);
      setTempDialog(null); setTempReading(''); setTempNote('');
      refreshToday();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const resolveCA = async (status) => {
    if (!caReason.trim()) return toast.error('Add a reason');
    try {
      await kApi().patch(`/corrective-actions/${caDialog.id}`, { reason: caReason, status });
      toast.success(`Marked ${status}`);
      setCaDialog(null); setCaReason('');
      refreshToday();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const submitSignoff = async () => {
    try {
      await kApi().post('/signoff', { notes: signoffNotes || null });
      toast.success('Day signed off');
      setSignoffDialog(false); setSignoffNotes('');
      refreshToday();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const markTaskDone = async (task) => {
    try {
      await kApi().post(`/cleaning/${task.id}/complete`, { status: 'done', missed_reason: null });
      toast.success('Task done ✓');
      refreshToday();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const markTaskMissed = async () => {
    if (!taskMissedReason.trim()) return toast.error('Add a reason if the task was missed');
    try {
      await kApi().post(`/cleaning/${taskDialog.id}/complete`, {
        status: 'missed', missed_reason: taskMissedReason,
      });
      toast.success('Task marked missed');
      setTaskDialog(null); setTaskMissedReason('');
      refreshToday();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  // ─── Renders ──────────────────────────────────────────────────────────────

  if (info?.error) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6">
      <div className="text-center"><AlertTriangle className="w-10 h-10 mx-auto mb-2 text-red-400" /><p>Kiosk not found. Check the URL.</p></div>
    </div>;
  }
  if (!info) return null;

  // PIN gate always dark for readability regardless of employee theme.
  if (!session) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col" data-testid="kiosk-pin-gate">
        <Toaster theme="dark" position="top-center" />
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md bg-slate-900 border-slate-800 text-slate-100">
            <CardContent className="p-8 space-y-6">
              <div className="text-center">
                <ShieldCheck className="w-12 h-12 mx-auto text-primary" />
                <h1 className="text-2xl font-bold mt-3 text-white">{info.location_name}</h1>
                <p className="text-sm text-slate-300 mt-1">Enter your PIN to start</p>
              </div>
              <Input type="password" inputMode="numeric" maxLength={8} value={pinInput}
                onChange={(e) => setPinInput(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && submitPin()}
                className="text-2xl text-center h-16 tracking-widest bg-slate-800 border-slate-700"
                placeholder="••••" autoFocus data-testid="kiosk-pin-input" />
              <div className="grid grid-cols-3 gap-2">
                {['1','2','3','4','5','6','7','8','9','','0','⌫'].map((k, i) => (
                  <Button key={i} variant="outline"
                    className="h-14 text-xl bg-slate-800 border-slate-700 hover:bg-slate-700"
                    onClick={() => {
                      if (k === '') return;
                      if (k === '⌫') setPinInput(p => p.slice(0, -1));
                      else if (pinInput.length < 8) setPinInput(p => p + k);
                    }}
                    disabled={k === ''} data-testid={`pad-${k}`}>
                    {k}
                  </Button>
                ))}
              </div>
              <Button className="w-full h-14 text-lg" onClick={submitPin} disabled={pinBusy} data-testid="kiosk-pin-submit">
                {pinBusy ? 'Checking…' : 'Enter'}
              </Button>
              <p className="text-xs text-center text-slate-400">SFBB-style records. Not a legal compliance guarantee.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const openCACount = today?.open_corrective_actions?.length || 0;
  const totalAssets = today?.assets?.length || 0;
  const checkedAssets = today?.assets?.filter(a => (today.checks_by_asset[a.id] || []).length > 0).length || 0;
  const signedOff = !!today?.signoff;

  // Theme classes
  const bg = t('bg-slate-950 text-white', 'bg-slate-50 text-slate-900');
  const headerBorder = t('border-slate-800', 'border-slate-200');
  const cardBg = t('bg-slate-900 border-slate-800 text-slate-100', 'bg-white border-slate-200 text-slate-900');
  const mutedText = t('text-slate-400', 'text-slate-500');
  const strongText = t('text-white', 'text-slate-900');
  const softButton = t('bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700 hover:text-white', 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100');
  const tabActive = t('bg-primary text-primary-foreground', 'bg-primary text-primary-foreground');
  const tabInactive = t('bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-700', 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100');

  const TabButton = ({ value, icon: Icon, children }) => (
    <button
      className={`px-4 py-2 rounded-full text-sm font-medium border flex items-center gap-2 transition-colors ${tab === value ? tabActive : tabInactive}`}
      onClick={() => setTab(value)}
      data-testid={`tab-${value}`}
    >
      <Icon className="w-4 h-4" /> {children}
    </button>
  );

  return (
    <div className={`min-h-screen ${bg}`} data-testid="kiosk-today" data-theme={theme}>
      <Toaster theme={isLight ? 'light' : 'dark'} position="top-center" />
      <header className={`border-b ${headerBorder} px-6 py-3 flex items-center justify-between`}>
        <div>
          <p className={`text-xs uppercase tracking-wide ${mutedText}`}>Today · {today?.date}</p>
          <h1 className={`text-xl font-bold ${strongText}`}>{info.location_name}</h1>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <p className={`text-sm ${strongText}`}>{session.employee_name}</p>
            {session.is_manager && <Badge variant="outline" className="bg-primary/20 border-primary/50 text-primary-foreground text-[10px]">Manager</Badge>}
          </div>
          <Button variant="outline" size="sm" onClick={toggleTheme} className={softButton} data-testid="kiosk-theme-toggle" aria-label="Toggle theme">
            {isLight ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
          </Button>
          <Button variant="outline" size="sm" onClick={lock} className={softButton} data-testid="kiosk-lock">
            <Lock className="w-3 h-3 mr-1" /> Lock
          </Button>
        </div>
      </header>

      {today?.strict_mode ? (
        <div className={`border-b px-6 py-2 text-sm flex items-center gap-2 ${t('bg-red-950 border-red-900 text-white', 'bg-red-50 border-red-200 text-red-800')}`}>
          <AlertTriangle className="w-4 h-4" /> Strict mode: sign-off blocked while corrective actions are open.
        </div>
      ) : (
        <div className={`border-b px-6 py-2 text-xs flex items-center gap-2 ${t('bg-amber-950/40 border-amber-900/40 text-amber-200', 'bg-amber-50 border-amber-200 text-amber-800')}`}>
          <ClipboardCheck className="w-4 h-4" /> Advisory mode (day {today?.days_since_start}/28) — sign-off allowed even with unresolved items.
        </div>
      )}

      <main className="p-6 space-y-6">
        {/* Status cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatusCard label="Temperature checks" value={`${checkedAssets}/${totalAssets}`} icon={Thermometer} tone={checkedAssets === totalAssets && totalAssets > 0 ? 'green' : 'amber'} testid="status-temps" isLight={isLight} />
          <StatusCard label="Corrective actions" value={String(openCACount)} icon={AlertTriangle} tone={openCACount === 0 ? 'green' : 'red'} testid="status-cas" isLight={isLight} />
          <StatusCard label="Day sign-off" value={signedOff ? 'Signed' : 'Pending'} icon={CheckCircle2} tone={signedOff ? 'green' : 'slate'} testid="status-signoff" isLight={isLight} />
          <StatusCard label="You" value={session.employee_name} icon={ShieldCheck} tone="slate" testid="status-you" isLight={isLight} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap" role="tablist">
          <TabButton value="temps" icon={Thermometer}>Temp checks</TabButton>
          <TabButton value="cleaning" icon={Brush}>Cleaning</TabButton>
          <TabButton value="stock" icon={ClipboardList}>Stock take</TabButton>
        </div>

        {tab === 'temps' && (
          <section data-testid="tab-panel-temps">
            {totalAssets === 0 ? (
              <p className={`text-sm ${mutedText}`}>No fridges/freezers configured yet. Ask the manager to add them from Health & Safety on desktop.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {today.assets.map(asset => {
                  const readings = today.checks_by_asset[asset.id] || [];
                  const latest = readings[readings.length - 1];
                  return (
                    <Card key={asset.id} className={cardBg} data-testid={`kiosk-asset-${asset.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className={`font-medium ${strongText}`}>{asset.name}</p>
                            <p className={`text-xs ${mutedText} capitalize`}>{asset.kind} · target {asset.target_min_c}° to {asset.target_max_c}°C</p>
                          </div>
                          {latest ? (
                            <Badge className={latest.passed ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}>{latest.reading_c}°C</Badge>
                          ) : (
                            <Badge variant="outline" className={t('border-slate-600 text-slate-300 bg-slate-800', 'border-slate-300 text-slate-600 bg-slate-100')}>Not checked</Badge>
                          )}
                        </div>
                        <Button className="w-full" onClick={() => setTempDialog(asset)} data-testid={`kiosk-temp-btn-${asset.id}`}>
                          <Thermometer className="w-4 h-4 mr-1" /> Add reading
                        </Button>
                        {readings.length > 1 && (
                          <p className={`text-xs ${mutedText} mt-2`}>{readings.length} readings today</p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {tab === 'cleaning' && (
          <section data-testid="tab-panel-cleaning">
            {(today?.cleaning_tasks?.length || 0) === 0 ? (
              <p className={`text-sm ${mutedText}`}>No cleaning tasks scheduled for today.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {today.cleaning_tasks.map(task => {
                  const done = today.cleaning_completions_by_task?.[task.id];
                  const cardCls = done?.status === 'done'
                    ? t('bg-green-950/40 border-green-800 text-slate-100', 'bg-green-50 border-green-200 text-slate-900')
                    : done?.status === 'missed'
                      ? t('bg-amber-950/40 border-amber-800 text-slate-100', 'bg-amber-50 border-amber-200 text-slate-900')
                      : cardBg;
                  return (
                    <Card key={task.id} className={cardCls} data-testid={`kiosk-task-${task.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="min-w-0">
                            <p className={`font-medium truncate ${strongText}`}>{task.name}</p>
                            <p className={`text-xs ${mutedText} capitalize`}>{task.schedule}</p>
                          </div>
                          {done ? (
                            <Badge className={done.status === 'done' ? 'bg-green-600 text-white' : 'bg-amber-600 text-white'}>{done.status}</Badge>
                          ) : (
                            <Badge variant="outline" className={t('border-slate-600 text-slate-300 bg-slate-800', 'border-slate-300 text-slate-600 bg-slate-100')}>Not done</Badge>
                          )}
                        </div>
                        {done ? (
                          <div className={`text-xs ${mutedText}`}>{done.employee_name}{done.missed_reason ? ` — "${done.missed_reason}"` : ''}</div>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={() => markTaskDone(task)} data-testid={`kiosk-task-done-btn-${task.id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Done
                            </Button>
                            <Button variant="outline" className={t('border-amber-600 text-amber-100 bg-amber-950/40 hover:bg-amber-900/60 hover:text-white', 'border-amber-500 text-amber-800 bg-amber-50 hover:bg-amber-100 hover:text-amber-900')} onClick={() => setTaskDialog(task)} data-testid={`kiosk-task-notdone-btn-${task.id}`}>
                              <X className="w-4 h-4 mr-1" /> Not done
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {tab === 'stock' && (
          <section data-testid="tab-panel-stock">
            <StockTakeTab
              kApi={kApi}
              theme={theme}
              session={session}
              refreshToday={refreshToday}
              sessionKey={session?.session_token}
            />
          </section>
        )}

        {/* Cross-cutting: open corrective actions */}
        {openCACount > 0 && (
          <section>
            <h2 className={`text-lg font-semibold mb-3 flex items-center gap-2 ${t('text-red-300', 'text-red-700')}`}><AlertTriangle className="w-5 h-5" /> Open corrective actions</h2>
            <div className="space-y-2">
              {today.open_corrective_actions.map(ca => (
                <Card key={ca.id} className={t('bg-red-950/40 border-red-800 text-slate-100', 'bg-red-50 border-red-200 text-slate-900')} data-testid={`kiosk-ca-${ca.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <p className={`text-sm ${strongText}`}>{ca.description}</p>
                    {session.is_manager ? (
                      <Button size="sm" onClick={() => setCaDialog(ca)} data-testid={`kiosk-ca-resolve-${ca.id}`}>Resolve</Button>
                    ) : (
                      <Badge variant="outline" className={t('border-red-700 text-red-200 bg-red-900/50', 'border-red-300 text-red-700 bg-red-100')}>Needs manager</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Cross-cutting: sign-off */}
        <section className="flex justify-end pt-4">
          {signedOff ? (
            <Badge className="bg-green-600 text-white text-lg py-2 px-4">✓ Signed off by {today.signoff.signed_by_name}</Badge>
          ) : session.is_manager ? (
            <Button size="lg" onClick={() => setSignoffDialog(true)} data-testid="kiosk-signoff-btn"><CheckCircle2 className="w-5 h-5 mr-1" /> Sign off today</Button>
          ) : (
            <Badge variant="outline" className={t('border-slate-600 text-slate-200 bg-slate-800 py-2 px-4', 'border-slate-300 text-slate-700 bg-slate-100 py-2 px-4')}>Waiting for manager sign-off</Badge>
          )}
        </section>
      </main>

      {/* Dialogs */}
      <Dialog open={!!tempDialog} onOpenChange={() => setTempDialog(null)}>
        <DialogContent className={t('bg-slate-900 border-slate-800 text-white', 'bg-white border-slate-200 text-slate-900')} data-testid="kiosk-temp-dialog">
          <DialogHeader><DialogTitle>{tempDialog?.name}</DialogTitle><DialogDescription className={mutedText}>Target {tempDialog?.target_min_c}° to {tempDialog?.target_max_c}°C</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className={mutedText}>Reading (°C)</Label>
              <Input type="number" step="0.1" value={tempReading} onChange={(e) => setTempReading(e.target.value)} className={`text-2xl h-14 ${t('bg-slate-800 border-slate-700', 'bg-white border-slate-300')}`} autoFocus data-testid="kiosk-temp-input" />
            </div>
            <div>
              <Label className={mutedText}>Note (optional)</Label>
              <Input value={tempNote} onChange={(e) => setTempNote(e.target.value)} className={t('bg-slate-800 border-slate-700', 'bg-white border-slate-300')} data-testid="kiosk-temp-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTempDialog(null)}>Cancel</Button>
            <Button onClick={submitTemperature} data-testid="kiosk-temp-submit">Save reading</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!caDialog} onOpenChange={() => setCaDialog(null)}>
        <DialogContent className={t('bg-slate-900 border-slate-800 text-white', 'bg-white border-slate-200 text-slate-900')} data-testid="kiosk-ca-dialog">
          <DialogHeader><DialogTitle>Resolve corrective action</DialogTitle><DialogDescription className={mutedText}>{caDialog?.description}</DialogDescription></DialogHeader>
          <div>
            <Label className={mutedText}>What was done? (food moved, engineer called, etc.)</Label>
            <Textarea value={caReason} onChange={(e) => setCaReason(e.target.value)} className={t('bg-slate-800 border-slate-700', 'bg-white border-slate-300')} rows={3} data-testid="kiosk-ca-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => resolveCA('deferred')} data-testid="kiosk-ca-defer">Defer</Button>
            <Button onClick={() => resolveCA('closed')} data-testid="kiosk-ca-close">Mark resolved</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!taskDialog} onOpenChange={() => { setTaskDialog(null); setTaskMissedReason(''); }}>
        <DialogContent className={t('bg-slate-900 border-slate-800 text-white', 'bg-white border-slate-200 text-slate-900')} data-testid="kiosk-task-dialog">
          <DialogHeader><DialogTitle>{taskDialog?.name}</DialogTitle><DialogDescription className={mutedText}>Why was this task not done? A reason is required so the EHO pack shows what happened.</DialogDescription></DialogHeader>
          <div>
            <Label className={mutedText}>Reason</Label>
            <Textarea value={taskMissedReason} onChange={(e) => setTaskMissedReason(e.target.value)} className={t('bg-slate-800 border-slate-700', 'bg-white border-slate-300')} rows={3} placeholder="e.g. Ran out of time, equipment out of order, not needed today" data-testid="kiosk-task-reason" autoFocus />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTaskDialog(null); setTaskMissedReason(''); }} data-testid="kiosk-task-cancel">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={markTaskMissed} data-testid="kiosk-task-missed"><X className="w-4 h-4 mr-1" /> Mark not done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={signoffDialog} onOpenChange={setSignoffDialog}>
        <DialogContent className={t('bg-slate-900 border-slate-800 text-white', 'bg-white border-slate-200 text-slate-900')} data-testid="kiosk-signoff-dialog">
          <DialogHeader><DialogTitle>Sign off today</DialogTitle><DialogDescription className={mutedText}>You are recording that {today?.date} is complete, on behalf of {info.location_name}. Attributed to {session.employee_name}. Edits leave an audit trail.</DialogDescription></DialogHeader>
          <div>
            <Label className={mutedText}>Notes (optional)</Label>
            <Textarea value={signoffNotes} onChange={(e) => setSignoffNotes(e.target.value)} className={t('bg-slate-800 border-slate-700', 'bg-white border-slate-300')} rows={3} data-testid="kiosk-signoff-notes" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignoffDialog(false)}>Cancel</Button>
            <Button onClick={submitSignoff} data-testid="kiosk-signoff-submit"><CheckCircle2 className="w-4 h-4 mr-1" /> Confirm sign-off</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusCard({ label, value, icon: Icon, tone, testid, isLight }) {
  const dark = {
    green: 'border-green-700 bg-green-950/40 text-green-100',
    amber: 'border-amber-700 bg-amber-950/40 text-amber-100',
    red: 'border-red-700 bg-red-950/40 text-red-100',
    slate: 'border-slate-700 bg-slate-900 text-slate-100',
  };
  const light = {
    green: 'border-green-200 bg-green-50 text-green-900',
    amber: 'border-amber-200 bg-amber-50 text-amber-900',
    red: 'border-red-200 bg-red-50 text-red-900',
    slate: 'border-slate-200 bg-white text-slate-900',
  };
  const cls = isLight ? light[tone] : dark[tone];
  return (
    <Card className={cls} data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="w-6 h-6 opacity-80" />
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
          <p className={`text-lg font-semibold truncate ${isLight ? 'text-slate-900' : 'text-white'}`}>{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}
